import { Fragment, type ReactNode } from 'react';
import styles from './MarkdownText.module.css';

// One capturing group around the alternation so String.split yields
// [text, token, text, token, ...]. Order matters at a position: bold, then a
// markdown [label](url) link, then a bare URL, then a bare email.
const TOKEN_RE =
  /(\*\*[\s\S]+?\*\*|__[\s\S]+?__|\[[^\]]+\]\([^)\s]+\)|https?:\/\/[^\s<>()]+|[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/;

const MD_LINK_RE = /^\[([^\]]+)\]\(([^)\s]+)\)$/;
const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const TRAILING_PUNCT_RE = /[.,;:!?)]+$/;

// A list line: optional indent, a bullet / dash / asterisk marker, a space, then
// the item text. Any of these markers renders as a clean bullet (so even a stray
// dash list shows as bullets, never a raw character on a run-on line).
const BULLET_RE = /^[ \t]*[•*-][ \t]+(.+?)[ \t]*$/;

// Only http(s) + mailto are ever turned into hrefs -- never javascript: etc.
function safeHref(url: string): string | null {
  if (/^https?:\/\//i.test(url) || /^mailto:/i.test(url)) return url;
  if (EMAIL_RE.test(url)) return `mailto:${url}`;
  return null;
}

function Link({ href, children }: { href: string; children: ReactNode }) {
  // External http(s) links open in a new tab; mailto opens the mail client.
  if (/^https?:\/\//i.test(href)) {
    return (
      <a className={styles.link} href={href} target="_blank" rel="noreferrer noopener">
        {children}
      </a>
    );
  }
  return <a className={styles.link} href={href}>{children}</a>;
}

/**
 * Inline tokenizer: bold, links, bare URLs, emails. EXPORTED so
 * MarkdownDocument reuses the same pass rather than reimplementing it — most
 * importantly `safeHref`, which is the only thing keeping a `javascript:` URL
 * out of an anchor. A second hand-rolled copy is how that protection gets
 * lost. Block structure is the renderers' own business; this is inline only.
 */
export function renderInline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  let key = 0;
  const parts = text.split(TOKEN_RE);
  for (let idx = 0; idx < parts.length; idx += 1) {
    const part = parts[idx];
    if (!part) continue;
    // Even indices are plain text between tokens; odd indices are tokens.
    if (idx % 2 === 0) { out.push(part); continue; }

    // Bold -- recurse so a link inside bold still links.
    if (
      part.length >= 4
      && ((part.startsWith('**') && part.endsWith('**'))
        || (part.startsWith('__') && part.endsWith('__')))
    ) {
      out.push(<strong key={key += 1}>{renderInline(part.slice(2, -2))}</strong>);
      continue;
    }

    // Markdown link [label](url).
    const md = MD_LINK_RE.exec(part);
    if (md) {
      const label = md[1] ?? '';
      const href = safeHref(md[2] ?? '');
      out.push(href ? <Link key={key += 1} href={href}>{label}</Link> : part);
      continue;
    }

    // Bare URL -- keep trailing sentence punctuation OUT of the href.
    if (/^https?:\/\//i.test(part)) {
      const trail = TRAILING_PUNCT_RE.exec(part);
      const tail = trail ? trail[0] : '';
      const url = tail ? part.slice(0, part.length - tail.length) : part;
      out.push(
        <Fragment key={key += 1}>
          <Link href={url}>{url}</Link>{tail}
        </Fragment>,
      );
      continue;
    }

    // Bare email -> mailto.
    if (EMAIL_RE.test(part)) {
      out.push(<Link key={key += 1} href={`mailto:${part}`}>{part}</Link>);
      continue;
    }

    out.push(part);
  }
  return out;
}

/**
 * Split the text into blocks: consecutive bullet lines become one clean
 * hanging-indent bullet list; everything else stays as inline text with its
 * newlines preserved (the caller's white-space: pre-wrap renders the breaks),
 * so the existing bold/link/newline rendering is unchanged. The list + bullet
 * are display:block spans -- valid inside the bubble's <p> and warning-free.
 */
function renderBlocks(text: string): ReactNode[] {
  const lines = text.split('\n');
  const out: ReactNode[] = [];
  let key = 0;
  let i = 0;
  while (i < lines.length) {
    if (BULLET_RE.test(lines[i] ?? '')) {
      const items: ReactNode[] = [];
      while (i < lines.length) {
        const m = BULLET_RE.exec(lines[i] ?? '');
        if (!m) break;
        items.push(
          <span key={key += 1} className={styles.bullet}>{renderInline(m[1] ?? '')}</span>,
        );
        i += 1;
      }
      out.push(<span key={key += 1} className={styles.list}>{items}</span>);
    } else {
      const buf: string[] = [];
      while (i < lines.length && !BULLET_RE.test(lines[i] ?? '')) {
        buf.push(lines[i] ?? '');
        i += 1;
      }
      out.push(<Fragment key={key += 1}>{renderInline(buf.join('\n'))}</Fragment>);
    }
  }
  return out;
}

export interface MarkdownTextProps {
  /** Raw assistant text, possibly containing markdown bold and links. */
  text: string;
  /** Class for the wrapping <p> (e.g. the chat's bubbleText style). */
  className?: string;
}

/**
 * Minimal inline-markdown renderer for EVIE chat answers. Renders **bold** /
 * __bold__ as <strong>, turns links into clickable anchors (a website URL or a
 * [label](url) markdown link opens in a new tab; an email becomes a mailto:),
 * and renders bullet-list lines (a leading •, -, or * marker) as a clean
 * hanging-indent bullet list. No dependency; only http(s)/mailto are ever linked
 * (never javascript:), and non-list text stays plain with newlines preserved by
 * the caller's white-space: pre-wrap. Shared by the admin + driver EVIE chats.
 */
export function MarkdownText({ text, className }: MarkdownTextProps) {
  return <p className={className}>{renderBlocks(text)}</p>;
}
